import { useState, useMemo } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  TableSortLabel,
  Card,
  CardContent,
} from "@mui/material"
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Work as WorkIcon,
  AccountBalance as AccountBalanceIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { Link, useNavigate } from "react-router-dom"

const CONSTITUTION_OPTIONS = ["Individual", "Firm", "Company", "LLP", "Other"]
const STATUS_OPTIONS = ["Active", "Inactive"]

function UsersPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState("")
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [orderBy, setOrderBy] = useState("createdAt")
  const [order, setOrder] = useState("desc")

  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Edit form state
  const [editFormData, setEditFormData] = useState({})

  const { data: usersData, isLoading, error, refetch } = useApiQuery(["users"], "/users/")
  
  const { data:{jobs: ongoingJobs} = {}, isLoading: jobsLoading } = useApiQuery(
    ["user-ongoing-jobs", selectedUser?._id],
    selectedUser ? `/users/${selectedUser._id}/ongoing-jobs` : null,
    { enabled: !!selectedUser }
  )

  const updateUserMutation = useApiMutation(`/users/${editFormData._id}`, "PATCH", {
    onSuccess: () => {
      refetch("users")
      handleCloseEditDialog()
    },
  })

  const deleteUserMutation = useApiMutation("/users", "DELETE", {
    onSuccess: () => {
      refetch()
      handleCloseDeleteDialog()
    },
  })

  const displayUsers = usersData || []

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget)
    setSelectedUser(user)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleViewDetails = () => {
    setViewDialogOpen(true)
    handleMenuClose()
  }

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setSelectedUser(null)
  }

  const handleEditUser = () => {
    setEditFormData({
      _id: selectedUser._id,
      name: selectedUser.name || "",
      phoneNumber: selectedUser.phoneNumber || "",
      panNumber: selectedUser.panNumber || "",
      constitution: selectedUser.constitution || "",
      tradeName: selectedUser.tradeName || "",
      address: selectedUser.address || "",
      gstinNumber: selectedUser.gstinNumber || "",
      status: selectedUser.status || "Active",
    })
    setEditDialogOpen(true)
    handleMenuClose()
  }

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false)
    setEditFormData({})
  }

  const handleDeleteUser = () => {
    setDeleteDialogOpen(true)
    handleMenuClose()
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setSelectedUser(null)
  }

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSaveEdit = async () => {
    try {
      // //console.log('handle Save Edit called with data:', editFormData)
      await updateUserMutation.mutateAsync({data: editFormData})
    } catch (error) {
      console.error("Update failed:", error)
    }
  }

  const handleConfirmDelete = async () => {
    try {
      await deleteUserMutation.mutateAsync({
        url: `/users/${selectedUser._id}`,
      })
    } catch (error) {
      console.error("Delete failed:", error)
    }
  }

  const formatCurrency = (amount) => {
    const numAmount = Number(amount) || 0
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(numAmount)
  }

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = displayUsers.filter(
      (user) =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.panNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phoneNumber?.includes(searchTerm)
    )

    filtered.sort((a, b) => {
      let aValue = a[orderBy]
      let bValue = b[orderBy]

      if (orderBy === "createdAt") {
        aValue = new Date(aValue)
        bValue = new Date(bValue)
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1
      if (aValue > bValue) return order === "asc" ? 1 : -1
      return 0
    })

    return filtered
  }, [displayUsers, searchTerm, orderBy, order])

  const paginatedUsers = useMemo(() => {
    return filteredAndSortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }, [filteredAndSortedUsers, page, rowsPerPage])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading users: {error.message}
      </Alert>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Users
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage all your users in one place
          </Typography>
        </Box>
        <Box>
          <Typography variant="h4" color="primary" sx={{textAlign:"center"}}>
            {displayUsers.length}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total Users
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={refetch} color="primary">
            <RefreshIcon />
          </IconButton>
          <Button component={Link} variant="contained" startIcon={<AddIcon />} to="/create-user">
            Add New User
          </Button>
        </Stack>
      </Box>

      {/* Search */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Search users by name, email, PAN, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleRequestSort("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Phone</TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleRequestSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={() => handleRequestSort("createdAt")}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user._id} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {user.name}
                  </Typography>
                </TableCell>
                <TableCell>{user.phoneNumber}</TableCell>
                <TableCell>
                  <Chip
                    label={user.status || "Active"}
                    color={user.status === "Active" ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                <TableCell align="center">
                  <IconButton onClick={(e) => handleMenuClick(e, user)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Typography variant="h6" color="textSecondary">
                    No users found
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {searchTerm ? "Try adjusting your search." : "Start by adding your first user."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredAndSortedUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewDetails}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditUser}>
          <EditIcon sx={{ mr: 1 }} />
          Edit User
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteUser} sx={{ color: "error.main" }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete User
        </MenuItem>
      </Menu>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">
              User Details
            </Typography>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedUser && (
            <Box>
              {/* User Information */}
              <Paper sx={{ p: 3, mb: 3, bgcolor: "grey.50" }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          User Name
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedUser.name}
                        </Typography>
                      </Box>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="caption" color="textSecondary">
                              Phone Number
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedUser.phoneNumber}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={selectedUser.status || "Active"}
                            color={selectedUser.status === "Active" ? "success" : "default"}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>

                  {selectedUser.address && (
                    <Grid item xs={12}>
                      <Stack direction="row" alignItems="flex-start" spacing={1}>
                        <LocationOnIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Address
                          </Typography>
                          <Typography variant="body1">{selectedUser.address}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Created At
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedUser.createdAt).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Ongoing Jobs */}
              <Paper sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={600}>
                    Ongoing Jobs
                  </Typography>
                  <Chip
                    label={`${ongoingJobs?.length || 0} active`}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />

                {jobsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : ongoingJobs && ongoingJobs.length > 0 ? (
                  <Grid container spacing={2}>
                    {ongoingJobs.map((job) => (
                      <Grid item xs={12} md={6} key={job.JobId}>
                        <Card variant="outlined">
                          <CardContent>
                            <Stack spacing={1}>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {job.jobTypeId.name || "N/A"}
                                </Typography>
                                <Chip
                                  label={job.status}
                                  size="small"
                                  color={job.status === "processing" ? "primary" : "info"}
                                />
                              </Box>
                              <Typography variant="caption" color="textSecondary">
                                Job ID: {job.JobId || ''}
                              </Typography>
                              <Divider />
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary">
                                  Fees:
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {formatCurrency(job.fees || 0)}
                                </Typography>
                              </Box>
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary">
                                  Paid:
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="success.main">
                                  {formatCurrency(job.totalFeesPaid || 0)}
                                </Typography>
                              </Box>
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary">
                                  Due:
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="error.main">
                                  {formatCurrency((job.fees || 0) - (job.totalFeesPaid || 0))}
                                </Typography>
                              </Box>
                              {job.dueDate && (
                                <Box display="flex" alignItems="center" spacing={0.5}>
                                  <CalendarIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                                  <Typography variant="caption" color="textSecondary">
                                    Due: {new Date(job.dueDate).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              )}
                              {/* <Button
                                size="small"
                                variant="outlined"
                                fullWidth
                                onClick={() => navigate(`/jobs/${JobId}`)}
                              >
                                View Job
                              </Button> */}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box textAlign="center" py={4}>
                    <WorkIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      No ongoing jobs
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      This user has no active jobs at the moment.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          <Button variant="contained" startIcon={<EditIcon />} onClick={handleEditUser}>
            Edit User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">
              Edit User
            </Typography>
            <IconButton onClick={handleCloseEditDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="User Name"
                value={editFormData.name || ""}
                onChange={(e) => handleEditFormChange("name", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={editFormData.phoneNumber || ""}
                onChange={(e) => handleEditFormChange("phoneNumber", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={editFormData.address || ""}
                onChange={(e) => handleEditFormChange("address", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editFormData.status || "Active"}
                  label="Status"
                  onChange={(e) => handleEditFormChange("status", e.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEditDialog} disabled={updateUserMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={updateUserMutation.isLoading}
            startIcon={updateUserMutation.isLoading ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {updateUserMutation.isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold" color="error">
            Delete User
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>{selectedUser?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            All associated data will be permanently removed from the system.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteUserMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteUserMutation.isLoading}
            startIcon={deleteUserMutation.isLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteUserMutation.isLoading ? "Deleting..." : "Delete User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UsersPage